import { useState, useMemo, type SyntheticEvent, useCallback } from "react";
import DOMPurify from "dompurify";
import { AlertCircle, Loader2, Mail } from "lucide-react";
import { cn } from "@/utilities/app-utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import { IDENTITY_PROVIDERS, type IdentityProvider } from "@/types/core";
import { GoogleIcon, AppleIcon } from "@/utilities/icon-utils";
import {
  validateEmail,
  validateEmailLoginForm
} from "@/utilities/validation-utils";

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"form">) {
  const { login, isLoading } = useAuth();

  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    if (touchedFields.has("email") && email) {
      const emailValidation = validateEmail(email);
      if (!emailValidation.isValid) {
        errors.email = emailValidation.error!;
      }
    }
    return errors;
  }, [email, touchedFields]);

  const handleInputChange = useCallback((value: string) => {
    setEmail(value);
    setTouchedFields((prev) => new Set(prev).add("email"));
  }, []);

  const handleBlur = useCallback(() => {
    setTouchedFields((prev) => new Set(prev).add("email"));
  }, []);

  const handleEmailLogin = useCallback(
    async (e: SyntheticEvent) => {
      e.preventDefault();
      if (!email) return;

      const sanitizedEmail = DOMPurify.sanitize(email.trim().toLowerCase());
      setError(null);
      setTouchedFields(new Set(["email"]));

      const emailValidation = validateEmailLoginForm({
        email: sanitizedEmail
      });
      if (!emailValidation.isValid) {
        setTouchedFields(new Set(["email"]));
        return;
      }

      try {
        await login(sanitizedEmail, IDENTITY_PROVIDERS.EMAIL);
      } catch (error) {
        console.error("Email login failed:", error);
        setError("Login failed, check your email & try again.");
      }
    },
    [email, login]
  );

  const handleProviderLogin = useCallback(
    async (emailValue: string, provider: IdentityProvider) => {
      setError(null);
      try {
        await login(emailValue, provider);
      } catch (error) {
        console.error(`${provider} login failed:`, error);
        setError(`${provider} login failed. Please try again.`);
      }
    },
    [login]
  );

  const isEmailValid = useMemo(
    () => email && validateEmail(email).isValid,
    [email]
  );

  return (
    <form
      className={cn("flex flex-col gap-6", className)}
      onSubmit={handleEmailLogin}
      {...props}
    >
      {/* Header */}
      <div className="flex flex-col items-center gap-2 text-center">
        <h2 className="text-xl font-bold tracking-tight text-slate-800 dark:text-foreground">
          Sign in or register to{" "}
          <span className="text-blue-600 dark:text-blue-400">
            Tempii
          </span>
        </h2>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-sm text-red-600 dark:text-red-400 login-fade-in-up">
          <AlertCircle className="size-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Email */}
      <div className="grid gap-5">
        <div className="grid gap-2">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/50 pointer-events-none" />
            <Input
              id="email"
              type="email"
              placeholder="email@domain.com"
              value={email}
              onChange={(e) => handleInputChange(e.target.value)}
              onBlur={handleBlur}
              required
              disabled={isLoading}
              className={cn(
                "pl-10 h-11 transition-all duration-200",
                validationErrors.email &&
                "border-destructive focus-visible:ring-destructive/20"
              )}
            />
          </div>
          {validationErrors.email && (
            <p className="text-xs text-destructive mt-0.5">
              {validationErrors.email}
            </p>
          )}
        </div>

        <Button
          type="submit"
          className={cn(
            "h-11 font-medium transition-all duration-200",
            "shadow-sm shadow-primary/20 hover:shadow-md hover:shadow-primary/25"
          )}
          disabled={isLoading || !isEmailValid}
        >
          {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          {isLoading ? "Signing in..." : "Sign in or register"}
        </Button>
      </div>

      {/* Divider */}
      <div className="relative my-1">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="px-3 text-slate-400 dark:text-foreground/70 tracking-wider bg-white/80 dark:bg-transparent rounded">
            Or continue with
          </span>
        </div>
      </div>

      {/* Social Login */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          type="button"
          variant="outline"
          title="Sign in or register with Google"
          onClick={() => handleProviderLogin(email, IDENTITY_PROVIDERS.GOOGLE)}
          disabled={isLoading}
          className={cn(
            "h-11 gap-2.5 font-medium transition-all duration-200",
            "border-slate-200 dark:border-border",
            "hover:bg-slate-50 dark:hover:bg-muted/60 hover:border-slate-300 dark:hover:border-border",
            "hover:shadow-sm"
          )}
        >
          <GoogleIcon className="size-4" />
          Google
        </Button>
        <Button
          type="button"
          variant="outline"
          title="Sign in or register with Apple"
          onClick={() => handleProviderLogin(email, IDENTITY_PROVIDERS.APPLE)}
          disabled={isLoading}
          className={cn(
            "h-11 gap-2 font-medium transition-all duration-200",
            "border-slate-200 dark:border-border",
            "hover:bg-slate-50 dark:hover:bg-muted/60 hover:border-slate-300 dark:hover:border-border",
            "hover:shadow-sm"
          )}
        >
          <AppleIcon className="size-4" />
          Apple
        </Button>
      </div>
    </form>
  );
}
